/**
 * Build URLs for the dynamic image-crop endpoint.
 *
 * The backend serves `{cropBase}/{src}__{ops}.{ext}` : if the derivative file
 * already exists it is served statically (no Drupal), otherwise it is generated
 * once from the encoded `ops` and cached. Because the URL is deterministic in
 * its params, the SAME requested format always maps to the SAME file — so a
 * format is generated exactly once.
 *
 * To make that "once" actually dedupe on disk, the front must request
 * *canonical* (quantized) names : we snap width/height to `STEP` here so two
 * near-identical viewports collapse onto one derivative. This mirrors the
 * server-side CropParamsNormalizer; the server still re-validates.
 *
 * `source` is the descriptor emitted by the decoupled_dynamic_image formatter:
 *   { src, scheme, cropBase, width, height, alt, title }
 * (a bare string is accepted too and treated as `src` under the public base).
 */

export const STEP = 20
export const MAX_DIMENSION = 4000
/**
 * Le pas du POINT D'INTÉRÊT, en pourcents — celui de `CropParamsNormalizer`
 * côté serveur. Grossier à dessein : chaque position est un fichier de plus sur
 * le disque, et un pas plus fin ne les multiplierait que pour un décalage que
 * personne ne voit.
 */
export const FOCUS_STEP = 5
const DEFAULT_EXT = 'webp'
const DEFAULT_PUBLIC_BASE = '/sites/default/files/dynamic'

/** True for an absolute http(s) URL (candidate for remote materialization). */
export function isRemoteUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url)
}

/**
 * FNV-1a 32-bit hex. MUST match DynamicImageController::fnv1a() so a remote
 * derivative path is deterministic from its source URL on both sides.
 */
export function fnv1a(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

/** Snap a dimension to the quantization grid and clamp it. 0/undefined = auto. */
export function quantize(value, step = STEP, max = MAX_DIMENSION) {
  const n = Math.round(Number(value) || 0)
  if (n <= 0) return 0
  return Math.min(max, Math.max(step, Math.round(n / step) * step))
}

/**
 * Le point d'intérêt canonique d'un cadrage : `{x, y}` en pourcents du DÉBORD,
 * calé sur le pas — ou null.
 *
 * Null pour un point CENTRÉ (50/50) et pour une op qui ne jette rien (`s`
 * garde toute l'image) : centré est exactement ce que l'op nue produit déjà,
 * l'encoder forkerait une seconde dérivée identique — c'est-à-dire regénérerait
 * toutes les images du site le jour où la fonction est arrivée. Le serveur
 * applique la MÊME règle (`CropParamsNormalizer::normalizeFocus`).
 */
export function normalizeFocus(focus, op = 'sc') {
  if (!focus || op === 's') return null
  const snap = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return 50
    return Math.min(100, Math.max(0, Math.round(n / FOCUS_STEP) * FOCUS_STEP))
  }
  const x = snap(focus.x)
  const y = snap(focus.y)
  return x === 50 && y === 50 ? null : { x, y }
}

/**
 * Compose a canonical ops string. `op` ∈ {s, c, sc, f}; `q` is optional.
 * Scale (`s`) may leave one side 0 (auto); crops need both.
 * `focus` `{x, y}` aims the crop (percent of the overflow) — cf. normalizeFocus.
 */
export function buildOps({ op = 'sc', w, h, q, focus } = {}) {
  const W = quantize(w)
  const H = quantize(h)
  let ops = `${op}_${W}_${H}`
  const point = normalizeFocus(focus, op)
  if (point) ops += `_p_${point.x}_${point.y}`
  if (q != null) ops += `_q_${Math.max(1, Math.min(100, Math.round(q)))}`
  return ops
}

function normalizeSource(source) {
  if (typeof source === 'string') {
    if (isRemoteUrl(source)) return { remote: source, cropBase: DEFAULT_PUBLIC_BASE }
    return { src: source.replace(/^\/+/, ''), cropBase: DEFAULT_PUBLIC_BASE }
  }
  const cropBase = (source?.cropBase || DEFAULT_PUBLIC_BASE).replace(/\/+$/, '')
  if (source?.src) return { src: String(source.src).replace(/^\/+/, ''), cropBase }
  if (isRemoteUrl(source?.url)) return { remote: source.url, cropBase }
  return { src: '', cropBase }
}

/**
 * Build one derivative URL.
 *
 * Local source → `{cropBase}/{src}__{ops}.{ext}`.
 * Remote source (allow-listed external URL) → `{cropBase}/_remote/{fnv1a(url)}__{ops}.{ext}?u={url}`;
 * the backend fetches + caches the original then crops it (or 302s to the
 * original if the host isn't allow-listed).
 *
 * @param {object|string} source  Formatter descriptor, a `src` string, or a URL.
 * @param {object|string} spec    `{op, w, h, q}` or a ready ops string.
 * @param {object} [opts]         `{ ext, token }`.
 */
export function cropUrl(source, spec, opts = {}) {
  const norm = normalizeSource(source)
  const ops = typeof spec === 'string' ? spec : buildOps(spec)
  const ext = opts.ext || DEFAULT_EXT
  const query = []
  let url
  if (norm.remote) {
    url = `${norm.cropBase}/_remote/${fnv1a(norm.remote)}__${ops}.${ext}`
    query.push(`u=${encodeURIComponent(norm.remote)}`)
  } else {
    if (!norm.src) return ''
    url = `${norm.cropBase}/${norm.src}__${ops}.${ext}`
  }
  if (opts.token) query.push(`gtok=${encodeURIComponent(opts.token)}`)
  return query.length ? `${url}?${query.join('&')}` : url
}

/**
 * Build a `srcset` string for responsive resolution switching.
 *
 * Two modes:
 *  - **width ladder** (preferred for responsive layouts): pass `opts.widths`,
 *    e.g. `[320, 640, 960, 1280]` — one entry per width, aspect ratio kept from
 *    `spec.w`/`spec.h`. Pair with a `sizes` attribute on the <img>.
 *  - **dpr** (fallback): scales the 1x box by each device-pixel-ratio.
 *
 * @param {object|string} source
 * @param {object} spec           `{op, w, h, q}` — the base box (gives the ratio).
 * @param {object} [opts]         `{ widths?, dprs = [1, 2], ext, token }`.
 * @returns {string} e.g. "…__sc_320_200.webp 320w, …__sc_640_400.webp 640w"
 */
export function cropSrcSet(source, spec, opts = {}) {
  const seen = new Set()
  const entries = []
  const push = (w, h) => {
    const descriptorW = w || h // width descriptor (fallback to height)
    if (!descriptorW || seen.has(descriptorW)) return
    seen.add(descriptorW)
    entries.push(`${cropUrl(source, { ...spec, w, h }, opts)} ${descriptorW}w`)
  }

  if (opts.widths && opts.widths.length) {
    const ratio = spec.w && spec.h ? spec.h / spec.w : 0
    for (const target of opts.widths) {
      const w = quantize(target)
      push(w, ratio ? quantize(Math.round(w * ratio)) : 0)
    }
    return entries.join(', ')
  }

  const dprs = opts.dprs && opts.dprs.length ? opts.dprs : [1, 2]
  for (const dpr of dprs) {
    push(quantize((spec.w || 0) * dpr), quantize((spec.h || 0) * dpr))
  }
  return entries.join(', ')
}
